import {
  UiBrandingWithOrgSchema,
  useQueryGetAllUiBrandings,
} from "@apis:ui_branding/ui_branding";
import { SuperDataGrid } from "@components:SuperDataGrid";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { Toggle } from "@components:Toggle";
import { useDebounce } from "@hooks:useDebounce";
import { usePaginationSortDataGrid } from "@hooks:usePaginationSortDataGrid";
import { useTableFilter } from "@hooks:useTableFilter";
import { Box, Button, InputAdornment, useTheme } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { Header } from "@pages:settings/Header";
import {
  useUiBrandingActions,
  useUiBrandingData,
} from "@stores:settings/ui-branding";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdSearch } from "react-icons/io";
import { MdClear, MdDelete, MdModeEdit, MdTune } from "react-icons/md";

import { ColorPreview } from "./ColorPreview";
import { LogoPreview } from "./LogoPreview";
import { UiBrandingCreateModal } from "./UiBrandingCreateModal";
import { UiBrandingDeleteModal } from "./UiBrandingDeleteModal";
import { UiBrandingModal } from "./UiBrandingModal";

export const UiBranding = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { searchKeyword, state } = useUiBrandingData();
  const { setSearchKeyword, setState, unMount } = useUiBrandingActions();

  const states = [
    { label: t("common.active"), value: "active" },
    { label: t("common.inactive"), value: "inactive" },
  ];

  const [selectedBranding, setSelectedBranding] =
    useState<UiBrandingWithOrgSchema | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [searchKey, setSearchKey] = useState("");

  useEffect(() => {
    return () => {
      unMount();
    };
  }, [unMount]);

  const onChange = () => {
    setSearchKeyword(searchKey);
  };

  const debouncedOnChange = useDebounce(onChange);

  const { paginationProps, sortProps } = usePaginationSortDataGrid({
    pageSize: 30,
    defaultSortModel: {
      field: "organizationName",
      sort: "asc",
    },
  });

  const { data: brandings = [], isLoading } = useQueryGetAllUiBrandings(
    state === "active",
  );

  // Filter by search keyword (organization name)
  const filteredBrandings = useTableFilter({
    filters: [
      {
        field: "organizationName",
        operator: "contains",
        value: searchKeyword,
      },
    ],
    data: brandings,
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "organizationName",
        headerName: t("preferences.uiBranding.organization"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "botName",
        headerName: t("preferences.uiBranding.bot"),
        flex: 1,
        minWidth: 140,
        valueGetter: (_, row) =>
          (row as UiBrandingWithOrgSchema).botName ??
          t("preferences.notApplicable"),
      },
      {
        field: "config.logoBase64",
        headerName: t("preferences.uiBranding.logo"),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.config?.logoBase64,
        renderCell: (params) => <LogoPreview logoBase64={params.value} />,
      },
      {
        field: "config.primaryColor",
        headerName: t("preferences.uiBranding.columnPrimaryColor"),
        width: 150,
        valueGetter: (_, row) => row.config?.primaryColor,
        renderCell: (params) => <ColorPreview color={params.value} />,
      },
      {
        field: "config.secondaryColor",
        headerName: t("preferences.uiBranding.columnSecondaryColor"),
        width: 150,
        valueGetter: (_, row) => row.config?.secondaryColor,
        renderCell: (params) => <ColorPreview color={params.value} />,
      },
      {
        field: "config.sidebarColor",
        headerName: t("preferences.uiBranding.columnSidebarColor"),
        width: 150,
        valueGetter: (_, row) => row.config?.sidebarColor,
        renderCell: (params) => <ColorPreview color={params.value} />,
      },
      {
        field: "config.backgroundColor",
        headerName: t("preferences.uiBranding.columnBackgroundColor"),
        width: 150,
        valueGetter: (_, row) => row.config?.backgroundColor,
        renderCell: (params) => <ColorPreview color={params.value} />,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 100,
        getActions: (params) => {
          const row = params.row as UiBrandingWithOrgSchema;
          const actions = [
            <GridActionsCellItem
              key="edit"
              icon={<MdModeEdit size={20} />}
              label={t("common.edit")}
              color="primary"
              onClick={() => {
                setSelectedBranding(row);
                setIsEditModalOpen(true);
              }}
            />,
          ];
          // Only show delete button for active brandings
          if (row.enabled) {
            actions.push(
              <GridActionsCellItem
                key="delete"
                icon={<MdDelete size={20} />}
                label={t("common.delete")}
                style={{ color: theme.palette.error.main }}
                onClick={() => {
                  setSelectedBranding(row);
                  setIsDeleteModalOpen(true);
                }}
              />,
            );
          }
          return actions;
        },
      },
    ],
    [t, theme.palette.error.main],
  );

  return (
    <Box
      className="Settings__UiBranding"
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "hidden",
        overflowX: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header
        leftSide={
          <Toggle state={state} allState={states} setState={setState} />
        }
        rightSide={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<MdTune />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t("preferences.uiBranding.add")}
            </Button>
            <SuperInput.TextField
              autoHeight={false}
              placeholder={t("preferences.uiBranding.searchPlaceholder")}
              value={searchKey}
              onChange={(e) => {
                debouncedOnChange();
                setSearchKey(e.target.value);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ backgroundColor: "transparent" }}
                    >
                      <IoMdSearch size={20} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        cursor: "pointer",
                        visibility: searchKeyword ? "visible" : "hidden",
                      }}
                      onClick={() => {
                        debouncedOnChange();
                        setSearchKey("");
                      }}
                    >
                      <MdClear size={20} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        }
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          pt: "2.5rem",
          px: "2.5rem",
        }}
      >
        <SuperDataGrid
          columns={columns}
          rows={filteredBrandings}
          loading={isLoading}
          {...paginationProps}
          {...sortProps}
          for="ui-branding"
          noRowsOverlayText={t("preferences.uiBranding.noneFound")}
          sx={{
            "& .MuiDataGrid-row--borderBottom": {
              backgroundColor: "white !important",
            },
          }}
        />
      </Box>
      {isEditModalOpen && selectedBranding && (
        <UiBrandingModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedBranding(null);
          }}
          branding={selectedBranding}
        />
      )}
      {isCreateModalOpen && (
        <UiBrandingCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
      {isDeleteModalOpen && selectedBranding && (
        <UiBrandingDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedBranding(null);
          }}
          brandingId={selectedBranding.id}
          organizationId={selectedBranding.organizationId}
          organizationName={selectedBranding.organizationName}
          botId={selectedBranding.botId}
          botName={selectedBranding.botName}
        />
      )}
    </Box>
  );
};
